import { useState, useEffect, useRef } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [infoVisible, setInfoVisible] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs1 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setHeaderVisible(true); }, { threshold: 0.1 });
    const obs2 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setFormVisible(true); }, { threshold: 0.05 });
    const obs3 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInfoVisible(true); }, { threshold: 0.05 });
    if (headerRef.current) obs1.observe(headerRef.current);
    if (formRef.current) obs2.observe(formRef.current);
    if (infoRef.current) obs3.observe(infoRef.current);
    return () => { obs1.disconnect(); obs2.disconnect(); obs3.disconnect(); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6eo432ohb161tfd96f0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isActive = (field: string) =>
    focusedField === field || Boolean(formData[field as keyof typeof formData]);

  const formFields = [
    { id: 'name', label: 'Полное имя', type: 'text' },
    { id: 'phone', label: 'Номер телефона', type: 'tel' },
    { id: 'email', label: 'Email адрес', type: 'email' },
  ];

  return (
    <section id="contact" className="py-14 md:py-20 bg-gradient-to-b from-white to-gray-50 overflow-visible">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header — slides up */}
        <div ref={headerRef} className={`text-center mb-10 md:mb-16 transition-all duration-800 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-4">
            Свяжитесь с нами
          </h2>
          <div className={`h-1 mx-auto rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-1000 delay-300 ${headerVisible ? 'w-24' : 'w-0'}`}></div>
          <p className={`text-sm md:text-lg text-gray-600 max-w-2xl mx-auto mt-3 transition-all duration-700 delay-200 ${headerVisible ? 'opacity-100' : 'opacity-0'}`}>
            Оставьте заявку на быстрый и надёжный ремонт бытовой техники
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
          {/* Form — slides from left */}
          <div ref={formRef} className={`transition-all duration-900 relative z-10 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`} style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-4 md:space-y-6">
              {formFields.map((field, idx) => (
                <div
                  key={field.id}
                  className={`relative transition-all duration-700 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${200 + idx * 120}ms` }}
                >
                  <label
                    htmlFor={field.id}
                    className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                      isActive(field.id)
                        ? '-top-2.5 text-xs bg-white px-2 text-teal-600 font-semibold'
                        : 'top-3 md:top-3.5 text-sm text-gray-500'
                    }`}
                  >
                    {field.label}
                  </label>
                  <input
                    type={field.type}
                    id={field.id}
                    name={field.id}
                    value={formData[field.id as keyof typeof formData]}
                    onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                    onFocus={() => setFocusedField(field.id)}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="w-full px-4 py-3 md:py-3.5 border-2 border-gray-300 rounded-lg focus:ring-0 focus:border-teal-500 transition-all duration-300 text-sm"
                  />
                </div>
              ))}

              {/* Service select */}
              <div
                className={`relative z-20 transition-all duration-700 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '560ms' }}
              >
                <label
                  htmlFor="service"
                  className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                    isActive('service')
                      ? '-top-2.5 text-xs bg-white px-2 text-teal-600 font-semibold'
                      : 'top-3 md:top-3.5 text-sm text-gray-500'
                  }`}
                >
                  Тип техники
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  onFocus={() => setFocusedField('service')}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="w-full px-4 py-3 md:py-3.5 border-2 border-gray-300 rounded-lg focus:ring-0 focus:border-teal-500 transition-all duration-300 text-sm appearance-none cursor-pointer bg-white relative z-20"
                >
                  <option value="">Выберите тип техники</option>
                  <option value="washing">Стиральная машина</option>
                  <option value="dishwasher">Посудомоечная машина</option>
                  <option value="dryer">Сушильная машина</option>
                  <option value="diagnostic">Диагностика</option>
                  <option value="installation">Установка и подключение</option>
                  <option value="emergency">Срочный ремонт</option>
                </select>
              </div>

              {/* Message */}
              <div
                className={`relative transition-all duration-700 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '680ms' }}
              >
                <label
                  htmlFor="message"
                  className={`absolute left-4 transition-all duration-300 pointer-events-none ${
                    isActive('message')
                      ? '-top-2.5 text-xs bg-white px-2 text-teal-600 font-semibold'
                      : 'top-3 md:top-3.5 text-sm text-gray-500'
                  }`}
                >
                  Сообщение
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 md:py-3.5 border-2 border-gray-300 rounded-lg focus:ring-0 focus:border-teal-500 transition-all duration-300 text-sm resize-none"
                />
                <div className="text-xs text-gray-500 mt-1 text-right">{formData.message.length}/500</div>
              </div>

              {/* Submit */}
              <div
                className={`transition-all duration-700 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '800ms' }}
              >
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="relative w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-3.5 md:py-4 rounded-lg font-semibold text-sm md:text-base hover:from-teal-600 hover:to-emerald-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap overflow-hidden group cursor-pointer hover:scale-[1.02] hover:shadow-xl"
                >
                  <span className="relative z-10">{isSubmitting ? 'Отправка...' : 'Отправить заявку'}</span>
                  <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm animate-fadeInUp">
                  Спасибо! Мы свяжемся с вами в ближайшее время.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm animate-fadeInUp">
                  Что-то пошло не так. Попробуйте снова или позвоните нам.
                </div>
              )}
            </form>
          </div>

          {/* Info side — slides from right */}
          <div ref={infoRef} className={`space-y-6 md:space-y-8 transition-all duration-900 ${infoVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`} style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
                Контактная информация
              </h3>
              <div className="space-y-4 md:space-y-6">
                {[
                  { icon: 'ri-phone-line', title: 'Телефон', content: '+7 (707) 432-50-20', href: 'tel:+77074325020' },
                  { icon: 'ri-mail-line', title: 'Email', content: 'info@serv1ce.kz', href: 'mailto:info@serv1ce.kz' },
                  { icon: 'ri-map-pin-line', title: 'Адрес', content: 'Алматы, Казахстан' },
                  { icon: 'ri-time-line', title: 'Часы работы', content: 'Пн-Пт: 8:00-22:00 | Сб-Вс: 9:00-00:00' },
                ].map((item, i) => (
                  <div
                    key={i}
                    className={`flex items-start group cursor-pointer transition-all duration-700 ${
                      infoVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
                    }`}
                    style={{ transitionDelay: `${200 + i * 150}ms` }}
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-lg flex items-center justify-center mr-3 md:mr-4 flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                      <i className={`${item.icon} text-teal-600 text-lg md:text-xl`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm md:text-base mb-0.5">{item.title}</h4>
                      {item.href ? (
                        <a href={item.href} className="text-xs md:text-sm text-gray-600 hover:text-teal-600 transition-colors">{item.content}</a>
                      ) : (
                        <p className="text-xs md:text-sm text-gray-600">{item.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Emergency card — scales in */}
            <div
              className={`bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl md:rounded-2xl p-5 md:p-8 text-white shadow-xl md:shadow-2xl transform hover:scale-[1.02] transition-all duration-700 cursor-pointer ${
                infoVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-85'
              }`}
              style={{ transitionDelay: '600ms', transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
            >
              <h4 className="text-lg md:text-xl font-bold mb-2 md:mb-4">Срочный ремонт 24/7</h4>
              <p className="text-teal-50 mb-4 md:mb-6 text-xs md:text-sm">
                Нужен срочный ремонт? Мы предлагаем круглосуточную службу экстренного ремонта.
              </p>
              <a
                href="tel:+77074325020"
                className="inline-flex items-center gap-2 bg-white text-teal-600 px-5 md:px-6 py-2.5 md:py-3 rounded-lg font-semibold text-sm hover:bg-gray-50 transition-all duration-300 whitespace-nowrap hover:scale-105"
              >
                <i className="ri-phone-fill" />
                Позвонить сейчас
              </a>
            </div>

            {/* Map — slides up */}
            <div
              className={`rounded-xl md:rounded-2xl overflow-hidden shadow-xl md:shadow-2xl h-48 md:h-64 transform hover:scale-[1.02] transition-all duration-700 ${
                infoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
              }`}
              style={{ transitionDelay: '800ms' }}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2906.6859894377!2d76.9286!3d43.2220!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDPCsDEzJzE5LjIiTiA3NsKwNTUnNDMuMCJF!5e0!3m2!1sen!2skz!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="serv1ce.kz Location"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
